import { Injectable } from '@angular/core';

import moment from 'moment-timezone';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Helpers } from '@recruitee/common';
import { HttpApiService } from '@recruitee/http';
import type { ResponseStatus, ViewboxInterviewEvent } from '@recruitee/interview-events-types';

import type { SchedulerAvailability } from './types';

@Injectable({ providedIn: 'root' })
export class ViewboxInterviewScheduleHttpClient {
  constructor(private httpApi: HttpApiService) {}

  public loadItem(
    scheduleToken: string,
    requestLinkToken: string,
    candidateTimezone: string,
    reschedule: boolean,
  ): Observable<SchedulerAvailability> {
    return this.httpApi
      .get(
        `/share/interview/availability/${scheduleToken}/${requestLinkToken}${
          reschedule ? '/reschedule' : ''
        }`,
        { candidate_timezone: candidateTimezone },
        { addToken: false },
      )
      .pipe(
        map(data => Helpers.fillInReferences(this.sortDates(data.availability), data.references)),
      );
  }

  public confirmSchedule(payload: {
    scheduleToken: string;
    requestLinkToken: string;
    reschedule: boolean;
    interviewEvent: {
      startsAt: string;
      candidateTimezone: string;
      candidateEmail?: string;
      rescheduleReason?: string;
    };
  }): Observable<SchedulerAvailability> {
    const path = `/share/interview/availability/${payload.scheduleToken}/${
      payload.requestLinkToken
    }${payload.reschedule ? '/reschedule' : ''}`;

    const options = {
      addToken: false,
    };

    const body = {
      interviewEvent: payload.interviewEvent,
    };

    return (
      payload.reschedule
        ? this.httpApi.patch(path, {}, body, options)
        : this.httpApi.post(path, {}, body, options)
    ).pipe(map(data => Helpers.fillInReferences(data.availability, data.references)));
  }

  public rsvp(payload: {
    eventToken: string;
    rsvpToken: string;
    responseStatus: ResponseStatus;
  }): Observable<ViewboxInterviewEvent> {
    return this.httpApi.patch(
      `/share/interview/events/${payload.eventToken}/${payload.rsvpToken}`,
      null,
      { responseStatus: payload.responseStatus },
      {
        addToken: false,
      },
    );
  }

  private sortDates(data: SchedulerAvailability): SchedulerAvailability {
    data.days = [...data.days].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

    return data;
  }
}
